import { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setGlobalLoading } from '../../../store/slices/uiSlice';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import {
  DashboardJob,
  CreateJobPayload,
  UpdateJobPayload,
  DeleteJobPayload,
} from '../types';
import {
  getDashboardJobsWithEdgeFunction,
  createJobWithEdgeFunction,
  updateJobWithEdgeFunction,
  deleteJobWithEdgeFunction,
} from '../api/jobsWithEdgeFunction';

export const useDashboardJobs = () => {
  const [jobs, setJobs] = useState<DashboardJob[]>([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await getDashboardJobsWithEdgeFunction();
      if (error) throw error;
      if (data) {
        setJobs(data);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard jobs:', err);
      showSnackbar('Failed to fetch jobs', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const createJob = useCallback(async (payload: CreateJobPayload) => {
    dispatch(setGlobalLoading({ isLoading: true, message: 'Creating job...' }));
    try {
      const { error } = await createJobWithEdgeFunction(payload);

      if (error) throw error;

      showSnackbar('Job created successfully', 'success');
      await fetchJobs(); // Refresh list
      return true;
    } catch (err: any) {
      console.error('Error creating job:', err);
      showSnackbar('Failed to create job', 'error');
      return false;
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  }, [dispatch, fetchJobs, showSnackbar]);

  const updateJob = useCallback(async (payload: UpdateJobPayload) => {
    dispatch(setGlobalLoading({ isLoading: true, message: 'Updating job...' }));
    try {
      // Extract job_id from payload as updateJobWithEdgeFunction expects it as a separate argument
      const { job_id, ...jobData } = payload;

      const { error } = await updateJobWithEdgeFunction(job_id, jobData);

      if (error) throw error;

      showSnackbar('Job updated successfully', 'success');
      await fetchJobs(); // Refresh list
      return true;
    } catch (err: any) {
      console.error('Error updating job:', err);
      showSnackbar('Failed to update job', 'error');
      return false;
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  }, [dispatch, fetchJobs, showSnackbar]);

  const deleteJob = useCallback(async (payload: DeleteJobPayload) => {
    dispatch(setGlobalLoading({ isLoading: true, message: 'Deleting job...' }));
    try {
      const { error } = await deleteJobWithEdgeFunction(payload.job_id);

      if (error) throw error;

      showSnackbar('Job deleted successfully', 'success');
      await fetchJobs(); // Refresh list
      return { success: true };
    } catch (err: any) {
      console.error('Error deleting job:', err); 
      showSnackbar('Failed to delete job', 'error');
      return { success: false, error: err };
    } finally {
      dispatch(setGlobalLoading({ isLoading: false }));
    }
  }, [dispatch, fetchJobs, showSnackbar]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  return {
    jobs,
    loading,
    fetchJobs,
    createJob,
    updateJob,
    deleteJob,
  };
};
